/**
 * ============================================
 * TATCHA PRODUCT SLIDER JAVASCRIPT
 * ============================================
 * 
 * 이 파일은 Tatcha 스타일의 제품 슬라이더 기능을 구현합니다.
 * 
 * 주요 기능:
 * - 좌우 화살표 네비게이션
 * - 터치 스와이프 지원
 * - 키보드 접근성
 * - 반응형 슬라이더
 * - ADD TO BAG 버튼 기능
 * 
 * 프리티코딩 수정 포인트:
 * 1. 슬라이더 설정: itemsToShow, itemsToShowMobile
 * 2. 애니메이션 속도: transitionSpeed
 * 3. 터치 감도: touchSensitivity
 */

class TatchaProductSlider {
    constructor(container) {
        this.container = container;
        this.slider = container.querySelector('.products-grid');
        this.prevBtn = container.querySelector('.slider-nav.prev');
        this.nextBtn = container.querySelector('.slider-nav.next');
        this.products = container.querySelectorAll('.product-card');
        this.addToBagButtons = container.querySelectorAll('.add-to-bag-btn');
        
        this.currentSlide = 0;
        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
        this.translateX = 0;
        
        this.init();
    }
    
    init() {
        if (!this.slider || !this.prevBtn || !this.nextBtn) {
            console.warn('Tatcha slider elements not found');
            return;
        }
        
        this.setupEventListeners();
        this.updateSlider();
        this.setupTouchEvents();
        this.setupKeyboardEvents();
    }
    
    setupEventListeners() {
        // 네비게이션 버튼 이벤트
        this.prevBtn.addEventListener('click', () => this.prevSlide());
        this.nextBtn.addEventListener('click', () => this.nextSlide());
        
        // ADD TO BAG 버튼 이벤트
        this.addToBagButtons.forEach(button => {
            button.addEventListener('click', (e) => this.handleAddToBag(e));
        });
        
        // 윈도우 리사이즈 이벤트
        window.addEventListener('resize', () => this.handleResize());
        
        // 터치 이벤트
        this.setupTouchEvents();
    }
    
    setupTouchEvents() {
        // 터치 시작
        this.slider.addEventListener('touchstart', (e) => {
            this.isDragging = true;
            this.startX = e.touches[0].clientX;
            this.currentX = this.startX;
            this.translateX = this.getCurrentTranslateX();
            
            this.slider.style.transition = 'none';
        });
        
        // 터치 이동
        this.slider.addEventListener('touchmove', (e) => {
            if (!this.isDragging) return;
            
            e.preventDefault();
            this.currentX = e.touches[0].clientX;
            const diff = this.currentX - this.startX;
            const translateX = this.translateX + diff;
            
            this.slider.style.transform = `translateX(${translateX}px)`;
        });
        
        // 터치 종료
        this.slider.addEventListener('touchend', () => {
            if (!this.isDragging) return;
            
            this.isDragging = false;
            this.slider.style.transition = 'transform 0.3s ease';
            
            const diff = this.currentX - this.startX;
            const threshold = 50; // 최소 스와이프 거리
            
            if (Math.abs(diff) > threshold) {
                if (diff > 0) {
                    this.prevSlide();
                } else {
                    this.nextSlide();
                }
            } else {
                this.updateSlider(); // 원래 위치로 복귀
            }
        });
    }
    
    setupKeyboardEvents() {
        // 키보드 네비게이션
        this.container.addEventListener('keydown', (e) => {
            switch(e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    this.prevSlide();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    this.nextSlide();
                    break;
                case 'Home':
                    e.preventDefault();
                    this.goToSlide(0);
                    break;
                case 'End':
                    e.preventDefault();
                    this.goToSlide(this.getMaxSlides());
                    break;
            }
        });
        
        // 포커스 관리
        this.addToBagButtons.forEach(button => {
            button.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleAddToBag(e);
                }
            });
        });
    }
    
    getItemsToShow() {
        return window.innerWidth >= 768 ? 4 : 2;
    }
    
    getMaxSlides() {
        const itemsToShow = this.getItemsToShow();
        return Math.max(0, this.products.length - itemsToShow);
    }
    
    getCurrentTranslateX() {
        const transform = this.slider.style.transform;
        const match = transform.match(/translateX\(([^)]+)px\)/);
        return match ? parseFloat(match[1]) : 0;
    }
    
    updateSlider() {
        const itemsToShow = this.getItemsToShow();
        const translateX = -this.currentSlide * (100 / itemsToShow);
        
        this.slider.style.transform = `translateX(${translateX}%)`;
        this.slider.setAttribute('data-current-slide', this.currentSlide);
        
        // 버튼 상태 업데이트
        this.updateNavigationButtons();
        
        // ARIA 라벨 업데이트
        this.updateAriaLabels();
    }
    
    updateNavigationButtons() {
        const maxSlides = this.getMaxSlides();
        
        this.prevBtn.disabled = this.currentSlide === 0;
        this.nextBtn.disabled = this.currentSlide >= maxSlides;
        
        this.prevBtn.style.opacity = this.currentSlide === 0 ? '0.5' : '1';
        this.nextBtn.style.opacity = this.currentSlide >= maxSlides ? '0.5' : '1';
    }
    
    updateAriaLabels() {
        const maxSlides = this.getMaxSlides();
        const totalSlides = maxSlides + 1;
        
        this.prevBtn.setAttribute('aria-label', `Previous slide (${this.currentSlide + 1} of ${totalSlides})`);
        this.nextBtn.setAttribute('aria-label', `Next slide (${this.currentSlide + 1} of ${totalSlides})`);
        
        // 현재 슬라이드 정보를 스크린 리더에게 알림
        this.container.setAttribute('aria-live', 'polite');
        this.container.setAttribute('aria-label', `Slide ${this.currentSlide + 1} of ${totalSlides}`);
    }
    
    prevSlide() {
        if (this.currentSlide > 0) {
            this.currentSlide--;
            this.updateSlider();
        }
    }
    
    nextSlide() {
        const maxSlides = this.getMaxSlides();
        if (this.currentSlide < maxSlides) {
            this.currentSlide++;
            this.updateSlider();
        }
    }
    
    goToSlide(index) {
        const maxSlides = this.getMaxSlides();
        this.currentSlide = Math.max(0, Math.min(index, maxSlides));
        this.updateSlider();
    }
    
    handleResize() {
        const newItemsToShow = this.getItemsToShow();
        const currentItemsToShow = this.slider.getAttribute('data-items-to-show') || 4;
        
        if (newItemsToShow !== parseInt(currentItemsToShow)) {
            this.currentSlide = 0;
            this.slider.setAttribute('data-items-to-show', newItemsToShow);
            this.updateSlider();
        }
    }
    
    handleAddToBag(e) {
        e.preventDefault();
        
        const button = e.currentTarget;
        const productId = button.getAttribute('data-product-id');
        const productUrl = button.getAttribute('data-product-url');
        
        // 버튼 상태 변경
        const originalText = button.textContent;
        button.textContent = 'ADDING...';
        button.disabled = true;
        
        // 실제 장바구니 추가 로직 (여기에 구현)
        this.addToCart(productId, productUrl).then(() => {
            // 성공 시
            button.textContent = 'ADDED!';
            setTimeout(() => {
                button.textContent = originalText;
                button.disabled = false;
            }, 2000);
        }).catch(() => {
            // 실패 시
            button.textContent = originalText;
            button.disabled = false;
        });
    }
    
    async addToCart(productId, productUrl) {
        try {
            // Shopify AJAX API를 사용한 장바구니 추가
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: productId,
                    quantity: 1
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to add to cart');
            }
            
            // 장바구니 업데이트 이벤트 발생
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { productId }
            }));
            
            return true;
        } catch (error) {
            console.error('Error adding to cart:', error);
            
            // 실패 시 제품 페이지로 이동
            if (productUrl && productUrl !== '#') {
                window.location.href = productUrl;
            }
            
            throw error;
        }
    }
    
    // 공개 메서드들
    getCurrentSlide() {
        return this.currentSlide;
    }
    
    getTotalSlides() {
        return this.products.length;
    }
    
    destroy() {
        // 이벤트 리스너 정리
        this.prevBtn.removeEventListener('click', this.prevSlide);
        this.nextBtn.removeEventListener('click', this.nextSlide);
        window.removeEventListener('resize', this.handleResize);
    }
}

// DOM 로드 완료 후 초기화
document.addEventListener('DOMContentLoaded', function() {
    const tatchaSliders = document.querySelectorAll('.tatcha-slider-section');
    
    tatchaSliders.forEach(container => {
        new TatchaProductSlider(container);
    });
});

// 동적 콘텐츠 로드 시 재초기화
document.addEventListener('shopify:section:load', function(event) {
    if (event.target.classList.contains('tatcha-slider-section')) {
        new TatchaProductSlider(event.target);
    }
});

// 장바구니 업데이트 이벤트 리스너
document.addEventListener('cart:updated', function(event) {
    console.log('Cart updated:', event.detail);
    
    // 여기에 장바구니 아이콘 업데이트 등의 로직 추가
    const cartIcon = document.querySelector('.cart-icon-bubble');
    if (cartIcon) {
        // 장바구니 아이콘 애니메이션
        cartIcon.classList.add('cart-updated');
        setTimeout(() => {
            cartIcon.classList.remove('cart-updated');
        }, 1000);
    }
});

// 접근성 개선을 위한 추가 기능
function setupSliderAccessibility() {
    const sliders = document.querySelectorAll('.tatcha-slider-section');
    
    sliders.forEach(slider => {
        // 슬라이더에 적절한 ARIA 속성 추가
        slider.setAttribute('role', 'region');
        slider.setAttribute('aria-label', 'Product recommendations');
        
        // 제품 카드에 포커스 관리
        const productCards = slider.querySelectorAll('.product-card');
        productCards.forEach((card, index) => {
            card.setAttribute('tabindex', '0');
            card.setAttribute('role', 'article');
            card.setAttribute('aria-label', `Product ${index + 1}`);
        });
    });
}

// 페이지 로드 시 접근성 설정
document.addEventListener('DOMContentLoaded', setupSliderAccessibility); 